import { Component, ReactNode } from 'react';
import { AlertTriangle, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * util_ ErrorBoundary — class component that catches render-time
 * exceptions (and lazy-chunk load failures) anywhere below it and
 * renders a friendly fallback UI with collapsible technical details.
 *
 * Wraps the root <App /> in main.tsx and each major route group in
 * App.tsx so a single broken page can never nuke the whole shell.
 */

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional label sent to the backend client-errors endpoint. */
  context?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack?: string } | null;
  detailsOpen: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, detailsOpen: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Always log to console for local dev visibility
    // eslint-disable-next-line no-console
    console.error('[util.ErrorBoundary]', error, errorInfo);
    this.setState({ errorInfo: { componentStack: errorInfo.componentStack ?? undefined } });

    // Best-effort: ship the error to the backend. Failure is silent —
    // we never want the error reporter to itself throw inside the boundary.
    try {
      const payload = {
        message: error.message,
        stack: error.stack,
        component_stack: errorInfo.componentStack,
        context: this.props.context ?? null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        ts: new Date().toISOString(),
      };
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
      fetch('/backend/api/v1/util/client-errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        /* swallow */
      });
    } catch {
      /* swallow */
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, detailsOpen: false });
  };

  private handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const { error, errorInfo, detailsOpen } = this.state;

    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="h-16 w-16 rounded-full bg-danger/10 flex items-center justify-center mb-4">
          <AlertTriangle size={32} className="text-danger" />
        </div>
        <h2 className="text-lg font-semibold mb-2">Coś poszło nie tak</h2>
        <p className="text-sm text-slate-500 max-w-md mb-6">
          Odśwież stronę, a jeśli problem się powtarza, skontaktuj się z administratorem.
        </p>
        <div className="flex items-center gap-2 mb-6">
          <button
            type="button"
            onClick={this.handleReload}
            className="btn-primary flex items-center gap-2"
          >
            <RotateCcw size={16} />
            Odśwież stronę
          </button>
          <button
            type="button"
            onClick={this.handleReset}
            className="btn-secondary"
          >
            Spróbuj ponownie
          </button>
        </div>

        {(error || errorInfo) && (
          <div className="w-full max-w-2xl text-left">
            <button
              type="button"
              onClick={() => this.setState({ detailsOpen: !detailsOpen })}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              {detailsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Szczegóły techniczne
            </button>
            {detailsOpen && (
              <pre className="mt-2 p-3 bg-slate-100 text-slate-700 text-[11px] rounded-md overflow-auto max-h-64 whitespace-pre-wrap">
                {error?.message}
                {error?.stack ? `\n\n${error.stack}` : ''}
                {errorInfo?.componentStack ? `\n\nComponent stack:${errorInfo.componentStack}` : ''}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  }
}
